import React, { useState, useEffect } from 'react';
import { FiSearch, FiArrowLeft } from 'react-icons/fi';
import { MdShoppingCart } from 'react-icons/md';
import { httpService } from '../../services/httpService';
import { toast } from 'react-toastify';
import config from "../../config";

const ProductCatalogDrawer = ({ 
  isOpen, 
  onClose, 
  onProductSelect,
  clientId,
  orderId 
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const imagesBaseUrl = config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL;

  useEffect(() => {
    if (isOpen) {
      // Reset state when drawer opens
      setProducts([]);
      setSearchTerm('');
      setHasSearched(false);
    }
  }, [isOpen]);

  // Search products when searchTerm changes
  useEffect(() => {
    const searchProducts = async () => {
      if (searchTerm.trim()) {
        setHasSearched(true);
        await handleSearchProducts(searchTerm.trim());
      } else {
        // When search is cleared, reset to initial state
        setProducts([]);
        setHasSearched(false);
      }
    };

    // Add a small debounce to prevent too many API calls
    const timeoutId = setTimeout(searchProducts, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleSearchProducts = async (query) => {
    setLoading(true);
    try {
      const response = await httpService.get(
        `/vendorproduct/searchAvailabelVendorProducts?fromSale=true&search=${encodeURIComponent(query)}`
      );
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error searching products:', error);
      toast.error('Failed to search products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (product) => {
    // Format the product data to match your appointment list structure
    const productData = {
      id: product._id,
      name: product.name,
      category: product.product_category?.name || 'Uncategorized',
      price: product.sale_price || product.mrp_price || product.purchase_price || 0,
      unit_price: product.sale_price || product.mrp_price || product.purchase_price || 0,
      quantity: 1,
      total_price: product.sale_price || product.mrp_price || product.purchase_price || 0,
      item_type: 'product',
      service_name: product.name,
      sku_number: product.sku_number,
      raw: product,
    };
    
    onProductSelect(productData);
    onClose();
  };

  // Helper function to get display price
  const displayPrice = (product) => {
    return Number(product.sale_price || product.mrp_price || product.purchase_price || 0);
  };

  // Helper function to check if product is available
  const isProductAvailable = (product) => {
    return (product.quantity_available || 0) > 0;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] transition-all duration-300 ease-in-out">
      <div className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full lg:w-2/5 bg-white shadow-xl transform transition-transform duration-300 ease-out">
        {/* Header - Updated with back button like POS */}
        <div className="flex justify-start items-center p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm gap-3">
          <button 
            onClick={onClose} 
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/70 transition-colors duration-200"
          >
            <FiArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
              <MdShoppingCart className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">Product Catalog</h2>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search products by name or SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Products Grid */}
        <div className="p-4 overflow-auto h-[calc(100vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((product) => {
                const available = isProductAvailable(product);
                const price = displayPrice(product);
                
                return (
                  <div
                    key={product._id}
                    className={`border p-3 rounded-lg shadow hover:shadow-md cursor-pointer ${
                      !available ? 'opacity-70 bg-gray-50' : 'bg-white'
                    }`}
                    onClick={() => {
                      handleProductSelect(product);
                      // Show warning if product is out of stock but still allow adding
                      if (!available) {
                        toast.warning('Product is out of stock');
                      }
                    }}
                  >
                    {/* Product Image/Icon */}
                    <div className="h-24 bg-gray-100 flex items-center justify-center mb-2 rounded">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={`${imagesBaseUrl}/${product.images[0]}`}
                          alt={product.name}
                          className="max-h-20 object-contain"
                        />
                      ) : (
                        <span style={{ fontSize: 28 }}>📦</span>
                      )}
                    </div>

                    {/* Product Name */}
                    <h3 className="font-medium text-gray-800 text-sm line-clamp-2 mb-1">
                      {product.name}
                    </h3>

                    {/* Category */}
                    <p className="text-gray-500 text-xs mb-2">
                      {product.product_category?.name || 'Uncategorized'}
                    </p>
                    
                    {/* Price and Stock Status - VERTICAL LAYOUT */}
                    <div className="space-y-1">
                      {/* Price or Out of Stock */}
                      {available ? (
                        <div className="font-bold text-green-600 text-lg">
                          ₹{price.toFixed(2)}
                        </div>
                      ) : (
                        <div className="font-bold text-red-600 text-sm">
                          Out of Stock
                        </div>
                      )}
                      
                      {/* Quantity Available - only show for available products */}
                      {available && (
                        <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full inline-block">
                          Qty Available: {product.quantity_available}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Empty State */
            <div className="flex items-center justify-center p-8 border rounded-lg bg-gray-50 h-64">
              <div className="text-center">
                <div className="text-4xl mb-2">🔍</div>
                <div className="font-medium mb-1">No products to show</div>
                <div className="text-sm text-gray-500">
                  {hasSearched 
                    ? 'No products found. Try adjusting your search terms.' 
                    : 'Search by name or SKU to display products here.'
                  }
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCatalogDrawer;